<script setup>
import { useSlots } from 'vue';
const {
  buttonClass,
  buttonOutlineColor,
  buttonText,
  textClass,
  buttonType,
  isDisabled,
} = defineProps({
  buttonClass: {
    type: String,
    required: false,
    default: '',
  },
  buttonOutlineColor: {
    type: String,
    required: false,
    default: 'outline-inherit',
  },
  buttonType: {
    type: String,
    required: false,
    default: 'button',
  },
  buttonText: {
    type: [String, Number],
    required: false,
    default: '',
  },
  textClass: {
    type: String,
    required: false,
    default: '',
  },
  isDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  title: {
    type: String,
    required: false,
    default: '',
  },
});
const slots = useSlots();
const emit = defineEmits(['hoverOn', 'hoverOff', 'buttonClick']);
</script>

<template>
  <button
    class="custom-button w-fit"
    :class="[buttonClass, buttonOutlineColor, { 'px-3': buttonText }]"
    :title="title"
    :type="buttonType"
    :disabled="isDisabled"
    @click="(e) => emit('buttonClick', e)"
  >
    <div
      v-if="slots.leftIcon"
      class="relative flex h-6 w-6 items-center justify-center"
    >
      <slot name="leftIcon"></slot>
    </div>
    <div
      v-if="buttonText.toString().length"
      :class="textClass"
    >
      {{ buttonText }}
    </div>
    <div
      v-if="slots.rightIcon"
      class="relative flex h-6 w-6 items-center justify-center"
    >
      <slot name="rightIcon"></slot>
    </div>
  </button>
</template>

<style scoped>
.custom-button {
  border: none;
  font: inherit;
  cursor: pointer;
}
.custom-button:disabled {
  cursor: not-allowed;
}
.primary-button {
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
  justify-content: center;
  align-items: center;
  color: var(--color-steel-1100);
  background-color: var(--color-sun-400);
}
.primary-button:hover {
  background-color: var(--color-sun-500);
}
.primary-button:active {
  background-color: var(--color-sun-600);
}
.primary-button:disabled {
  color: var(--color-steel-700);
  background-color: var(--color-steel-100);
}
.secondary-button {
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
  justify-content: center;
  align-items: center;
  color: var(--color-steel-200);
  background-color: var(--color-steel-900);
}
.secondary-button:hover {
  background-color: var(--color-steel-1000);
}
.secondary-button:active {
  background-color: var(--color-steel-1100);
}
.secondary-button:disabled {
  color: var(--color-steel-400);
  background-color: var(--color-steel-900);
}
.default-button {
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
  justify-content: center;
  align-items: center;
  color: var(--color-steel-900);
  background-color: var(--color-neutral-100-a);
}
.default-button:hover {
  background-color: var(--color-steel-300);
}
.default-button:active {
  background-color: var(--color-steel-400);
}
.default-button:disabled {
  color: var(--color-steel-700);
}
.subtle-button {
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
  justify-content: center;
  align-items: center;
  color: var(--color-steel-900);
  background-color: white;
}
.subtle-button:hover {
  background-color: var(--color-steel-300);
}
.subtle-button:active {
  background-color: var(--color-steel-400);
}
.subtle-button:disabled {
  color: var(--color-steel-700);
}
</style>
